import React, { useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  SxProps,
  TextField,
  Theme,
  Tooltip,
  alpha,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

export type CompactSortDirection = 'asc' | 'desc';

export interface CompactSortOption<T extends string = string> {
  value: T;
  label: string;
}

interface CompactSearchSortControlProps<T extends string = string> {
  query: string;
  onQueryChange: (value: string) => void;
  onQuerySubmit?: () => void;
  placeholder: string;
  sortKey: T;
  sortDirection: CompactSortDirection;
  sortOptions: CompactSortOption<T>[];
  onSortKeyChange: (value: T) => void;
  onSortDirectionChange: (value: CompactSortDirection) => void;
  leadingAction?: React.ReactNode;
  sx?: SxProps<Theme>;
}

function CompactSearchSortControl<T extends string = string>({
  query,
  onQueryChange,
  onQuerySubmit,
  placeholder,
  sortKey,
  sortDirection,
  sortOptions,
  onSortKeyChange,
  onSortDirectionChange,
  leadingAction,
  sx,
}: CompactSearchSortControlProps<T>) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const activeSortOption = useMemo(
    () => sortOptions.find((option) => option.value === sortKey) ?? sortOptions[0],
    [sortKey, sortOptions]
  );

  const handleSortMenuClose = () => setMenuAnchor(null);

  return (
    <Box
      sx={[
        {
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          minWidth: 0,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {leadingAction ? <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{leadingAction}</Box> : null}

      <TextField
        fullWidth
        size="small"
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') onQuerySubmit?.();
        }}
        placeholder={placeholder}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchRoundedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            </InputAdornment>
          ),
        }}
        sx={{
          minWidth: 0,
          '& .MuiOutlinedInput-root': {
            minHeight: 30,
            borderRadius: 1.5,
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.96),
            boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
            fontSize: '0.82rem',
            '& fieldset': {
              borderColor: (theme) => alpha(theme.palette.divider, 0.8),
            },
            '&:hover fieldset': {
              borderColor: 'text.secondary',
            },
          },
          '& .MuiInputBase-input': {
            py: '6px',
          },
        }}
      />

      <Tooltip title={sortDirection === 'asc' ? 'Sorting ascending' : 'Sorting descending'}>
        <IconButton
          size="small"
          onClick={() => onSortDirectionChange(sortDirection === 'asc' ? 'desc' : 'asc')}
          aria-label={sortDirection === 'asc' ? 'Sort ascending' : 'Sort descending'}
          sx={{
            width: 30,
            height: 30,
            flexShrink: 0,
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            boxShadow: '0 1px 2px rgba(15,23,42,0.05)',
          }}
        >
          {sortDirection === 'asc' ? (
            <ArrowUpwardRoundedIcon sx={{ fontSize: 14 }} />
          ) : (
            <ArrowDownwardRoundedIcon sx={{ fontSize: 14 }} />
          )}
        </IconButton>
      </Tooltip>

      <Tooltip title={activeSortOption ? `Sort by ${activeSortOption.label}` : 'Choose sort field'}>
        <IconButton
          size="small"
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          aria-label="Choose sort field"
          sx={{
            width: 30,
            height: 30,
            flexShrink: 0,
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            boxShadow: '0 1px 2px rgba(15,23,42,0.05)',
          }}
        >
          <TuneRoundedIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleSortMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 0.8,
            minWidth: 220,
            borderRadius: 2.5,
            boxShadow: '0 18px 38px rgba(15,23,42,0.14)',
          },
        }}
      >
        {sortOptions.map((option) => {
          const selected = option.value === sortKey;
          return (
            <MenuItem
              key={option.value}
              selected={selected}
              onClick={() => {
                onSortKeyChange(option.value);
                handleSortMenuClose();
              }}
              sx={{ minHeight: 40 }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                {selected ? <CheckRoundedIcon sx={{ fontSize: 18 }} /> : null}
              </ListItemIcon>
              <ListItemText primary={option.label} />
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
}

export default CompactSearchSortControl;
